f<template>
  <ul v-if="paginationPages.length > 1" class="flex flex-wrap justify-center gap-2 p-0 m-0">
    <!-- Back -->
    <li class="p-item" @click="modelValue > 1 && changePage(modelValue - 1)">
      <UiIcon icon="chevron-left" :size="18" />
    </li>

    <li
      v-for="(pag, idx) in paginationPages"
      :key="`${pag}-${idx}`"
      class="p-item group"
      :class="{ 'bg-[--c-primary-500] font-semibold': pag === modelValue }"
      @click="changePage(pag)"
    >
      <div v-if="typeof pag === 'number'">{{ pag }}</div>
      <div v-else>
        <UiIcon v-if="pag === 'fb' || pag === 'ff'" icon="dots" :size="18" class="group-hover:hidden" />
        <UiIcon :icon="pag === 'fb' ? 'chevrons-left' : 'chevrons-right'" :size="18" class="hidden group-hover:block" />
      </div>
    </li>

    <!-- Forward -->
    <li class="p-item" @click="modelValue < pages && changePage(modelValue + 1)">
      <UiIcon icon="chevron-right" :size="18" />
    </li>

    <li v-if="!hideJumpTo && pages > 1">
      <UiInput
        v-model="value"
        type="number"
        :min="1"
        :max="pages"
        size="small"
      />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { clamp } from '@/common/util'

import UiIcon from '@/components/ui/icon.vue'
import UiInput from '@/components/ui/input.vue'

const props = defineProps<{
  modelValue: number
  pages: number
  hideJumpTo?: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', clamp(val, 1, props.pages))
})

const paginationPages = computed(() => {
  const arr = []

  if (props.pages <= 7) {
    for (let i = 1; i <= props.pages; i++) {
      arr.push(i)
    }
  } else {
    if (props.modelValue <= 7) {
      for (let i = 1; i <= 7; i++) {
        arr.push(i)
      }
      arr.push('ff')
      arr.push(props.pages)
    } else if (props.modelValue >= props.pages - 2) {
      arr.push(1)
      arr.push('fb')
      for (let i = props.pages - 4; i <= props.pages; i++) {
        arr.push(i)
      }
    } else {
      arr.push(1)
      arr.push('fb')
      for (let i = props.modelValue - 2; i <= props.modelValue + 2; i++) {
        arr.push(i)
      }
      arr.push('ff')
      arr.push(props.pages)
    }
  }

  return arr
})

watch(
  () => props.modelValue,
  () => {
    if (props.modelValue > props.pages) emit('update:modelValue', props.pages)
    else if (props.modelValue < 1) emit('update:modelValue', 1)
  }
)

const changePage = (page: number | string) => {
  if (page === 'fb') page = props.modelValue - 5
  if (page === 'ff') page = props.modelValue + 5
  emit('update:modelValue', page)
}
</script>

<style scoped lang="scss">
.p-item {
  @apply text-white text-sm inline-flex min-w-[32px] h-8 items-center justify-center cursor-pointer hover:bg-[--c-primary-100] dark:hover:bg-[--c-primary-700] rounded-full transition-colors;
}
</style>
